import React, { useState, useEffect, useRef } from 'react';
import '../styles.css';

const ConsentModal = () => {
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        const consentGiven = localStorage.getItem('userConsent');

        // ✅ Ensure the modal remains open until explicit consent is given
        if (!consentGiven || consentGiven === 'pending') {
            setShowModal(true);
            localStorage.setItem('userConsent', 'pending'); // Prevents bypassing
        }

        const handleStorageChange = (event) => {
            if (event.key === 'userConsent' && event.newValue !== 'pending') {
                setShowModal(false);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const handleConsent = (type) => {
        localStorage.setItem('userConsent', type);
        setShowModal(false);

        if (window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': type === 'accepted' ? 'granted' : 'denied',
                'analytics_storage': type === 'accepted' ? 'granted' : 'denied'
            });
        }

        if (window.CookieConsent) {
            if (type === 'accepted') {
                window.CookieConsent.acceptAllCookies();
            } else {
                window.CookieConsent.declineAllCookies();
            }
        }

        // ✅ Properly clear cookies when user declines consent
        if (type === 'declined') {
            document.cookie.split(';').forEach((cookie) => {
                const cookieName = cookie.split('=')[0].trim();
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            });
        }
    };

    useEffect(() => {
        if (showModal) {
            const focusableElements = modalRef.current.querySelectorAll('button, a');
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            const handleKeyDown = (e) => {
                if (e.key === 'Tab') {
                    if (e.shiftKey && document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    } else if (!e.shiftKey && document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
                if (e.key === 'Escape') {
                    setShowModal(false);
                }
            };

            document.addEventListener('keydown', handleKeyDown);
            firstElement.focus();

            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [showModal]);

    if (!showModal) return null;

    return (
        <div className={`consent-modal ${showModal ? 'consent-modal--active' : ''}`} ref={modalRef} aria-hidden={!showModal} role="dialog" aria-labelledby="consent-title">
            <div className="consent-modal__overlay">
                <div className="consent-modal__content">
                    <h2 id="consent-title" className="consent-modal__title">Your Privacy Matters</h2>
                    <p className="consent-modal__description" aria-live="polite">
                        We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. 
                        By continuing to use this site, you consent to our use of cookies as described in our <a href="/privacy-policy" className="consent-modal__link">Privacy Policy</a>.
                    </p>
                    <div className="consent-modal__actions">
                        <button 
                            onClick={() => handleConsent('accepted')} 
                            className="consent-modal__button consent-modal__button--accept"
                        >
                            Accept All Cookies
                        </button>
                        <button 
                            onClick={() => handleConsent('declined')} 
                            className="consent-modal__button consent-modal__button--decline"
                        >
                            Reject All Cookies
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentModal;
